/**
 * Building blocks shared by the Grade 11–12 templates (grade11.ts, grade12.ts).
 *
 * Scoped to this grade range (hence the file name) so it never collides with
 * helpers owned by other grade files. Everything here is pure; the only
 * randomness is the Rng a template passes in, so generation stays deterministic.
 */
import { isInteger, key, rat, type Rational } from '../../math/rational.ts';
import type { AnswerKind } from '../answer.ts';
import { toPlain, type MathMarkup } from '../markup.ts';
import type { GeneratedProblemCore } from '../../types.ts';

export type Instruction = NonNullable<GeneratedProblemCore['instruction']>;

/** Ideal visible prompt width on a phone (the hard cap is 32 via toPlain). */
export const PHONE_CHARS = 28;

/** Largest misconception value worth offering as a choice; bigger ones give themselves away. */
export const SHOWABLE = 9999;

// ───────────────────────────── Difficulty & sampling ─────────────────────────────

function clamp01(difficulty: number): number {
  return Number.isNaN(difficulty) ? 0 : Math.min(1, Math.max(0, difficulty));
}

/** Whole-number bound interpolated from `easy` (difficulty 0) to `hard` (difficulty 1). */
export function scaled(difficulty: number, easy: number, hard: number): number {
  return Math.round(easy + (hard - easy) * clamp01(difficulty));
}

/**
 * Rejection sampling: call `attempt` until it returns a problem (non-null).
 * Templates state their constraints as "draw, then reject what doesn't fit",
 * which reads far more clearly than constructive number theory. `fallback` is a
 * fixed known-good problem used only if every attempt is rejected; the draw
 * ranges are chosen so that practically never happens.
 */
export function retry<T>(attempts: number, attempt: () => T | null, fallback: () => T): T {
  for (let i = 0; i < attempts; i++) {
    const out = attempt();
    if (out !== null) return out;
  }
  return fallback();
}

/** True when the prompt fits the ideal phone width. */
export function fitsPhone(prompt: MathMarkup): boolean {
  return toPlain(prompt).length <= PHONE_CHARS;
}

// ───────────────────────────── Integer utilities ─────────────────────────────

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x;
}

/** base^exp for a whole exponent ≥ 0, by repeated multiplication (exact for the small values used here). */
export function ipow(base: number, exp: number): number {
  let out = 1;
  for (let i = 0; i < exp; i++) out *= base;
  return out;
}

/** Exact rational power with a whole (possibly negative) exponent: (2, −3) → 1/8. */
export function rpow(base: Rational, exp: number): Rational {
  const n = ipow(base.n, Math.abs(exp));
  const d = ipow(base.d, Math.abs(exp));
  return exp >= 0 ? rat(n, d) : rat(d, n);
}

/**
 * base^exp (whole exp ≥ 0), or null once the numerator or denominator passes
 * `limit` — keeps misconception values readable and inside exact integer range.
 */
export function cappedPow(base: Rational, exp: number, limit: number): Rational | null {
  let n = 1;
  let d = 1;
  for (let i = 0; i < exp; i++) {
    n *= base.n;
    d *= base.d;
    if (Math.abs(n) > limit || d > limit) return null;
  }
  return rat(n, d);
}

/** Largest s with s² ≤ n (n ≥ 0). */
export function isqrt(n: number): number {
  let s = 0;
  while ((s + 1) * (s + 1) <= n) s++;
  return s;
}

/** √n rounded to the nearest whole number, without floating point: round up iff n > s² + s. */
export function roundSqrt(n: number): number {
  const s = isqrt(n);
  return n > s * s + s ? s + 1 : s;
}

/** Whole numbers are 'integer'; anything else is shown as a fraction. */
export function kindOf(v: Rational): AnswerKind {
  return isInteger(v) ? 'integer' : 'fraction';
}

// ───────────────────────────── Markup formatting ─────────────────────────────

/** One polynomial term: coefficient × variable^power. */
export type Term = readonly [coef: number, power: number];

/**
 * Polynomial markup in the given term order, e.g. [[2, 2], [-7, 1], [3, 0]] → `2x^2 - 7x + 3`.
 * Zero terms are skipped (never `x + 0` or `0x`), unit coefficients are hidden
 * (`x`, `-x`, never `1x`), and a negative after the first term is written as
 * subtraction (`- 3`, never `+ -3`).
 */
export function poly(terms: readonly Term[], v = 'x'): MathMarkup {
  let out = '';
  for (const [coef, power] of terms) {
    if (coef === 0) continue;
    const size = Math.abs(coef);
    const variable = power === 0 ? '' : power === 1 ? v : power < 10 ? `${v}^${power}` : `${v}^{${power}}`;
    const body = power === 0 ? `${size}` : `${size === 1 ? '' : size}${variable}`;
    if (!out) out = coef < 0 ? `-${body}` : body;
    else out += coef < 0 ? ` - ${body}` : ` + ${body}`;
  }
  return out || '0';
}

/** A rational constant: `7`, `-7`, `\frac{1}{2}`, `-\frac{1}{2}`. */
export function ratMarkup(v: Rational): MathMarkup {
  if (v.d === 1) return `${v.n}`;
  return `${v.n < 0 ? '-' : ''}\\frac{${Math.abs(v.n)}}{${v.d}}`;
}

/** `\log` for base 10 (the CAPS convention), `\log_{b}` otherwise. */
export function logOf(base: number): MathMarkup {
  return base === 10 ? '\\log' : `\\log_{${base}}`;
}

/** A term of a displayed sequence/series after the first: ` + 7` or ` - 7`. */
export function seriesTerm(n: number): MathMarkup {
  return n < 0 ? ` - ${-n}` : ` + ${n}`;
}

// ───────────────────────────── Problem assembly ─────────────────────────────

/** A misconception value; null/undefined = "does not apply here". */
export type Candidate = Rational | null | undefined;

export interface DistractorOptions {
  /** Drop zero and negative misconceptions (templates whose answers are always positive). */
  positiveOnly?: boolean;
  /** Keep fractional misconceptions on a par with whole ones, even for a whole-number answer. */
  keepFractions?: boolean;
}

/**
 * Clean a template's misconception list into its distractors: drops
 * non-applicable entries, duplicates and the answer itself (and non-positive
 * values when `positiveOnly`). When the answer is a whole number, whole-number
 * misconceptions come first — a lone fraction among integer choices is an easy
 * give-away — and fractional ones are kept only while fewer than two remain,
 * unless `keepFractions` says fractions are natural answers for the skill
 * (e.g. the other root of 2x² − 7x + 6 = 0 is 3/2 even when the asked root is 2).
 */
export function misconceptions(
  answer: Rational,
  candidates: readonly Candidate[],
  opts: DistractorOptions = {},
): Rational[] {
  const seen = new Set<string>([key(answer)]);
  const preferred: Rational[] = [];
  const reserve: Rational[] = [];
  for (const c of candidates) {
    if (!c || seen.has(key(c))) continue;
    if (opts.positiveOnly && c.n <= 0) continue;
    seen.add(key(c));
    (isInteger(answer) && !isInteger(c) && !opts.keepFractions ? reserve : preferred).push(c);
  }
  return [...preferred, ...reserve.slice(0, Math.max(0, 2 - preferred.length))];
}

/** Assemble the template output; answerKind follows the value. */
export function problem(
  prompt: MathMarkup,
  answer: Rational,
  candidates: readonly Candidate[],
  instruction?: Instruction,
  opts: DistractorOptions = {},
): GeneratedProblemCore {
  return {
    prompt,
    ...(instruction ? { instruction } : {}),
    answer,
    answerKind: kindOf(answer),
    distractors: misconceptions(answer, candidates, opts),
  };
}

/** `rat(n, d)` when the numbers are safe and d ≠ 0, else null (for misconceptions that can blow up). */
export function safeRat(n: number, d = 1): Rational | null {
  if (d === 0 || !Number.isSafeInteger(n) || !Number.isSafeInteger(d)) return null;
  if (Math.abs(n) > 1_000_000 || Math.abs(d) > 1_000_000) return null;
  return rat(n, d);
}
